import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface SettlementVerification {
  id?: string | number;
  status?: string;
  week_ending?: string;
  terminal?: string;
  isp_number?: string;
  settlement_amount?: number | string;
  deposit_amount?: number | string;
  variance?: number | null;
  lag_days?: number | null;
}

export interface StatusCounts {
  total: number;
  verified: number;
  pending: number;
  missing: number;
  variance: number;
}

interface SettlementResponse {
  verifications?: SettlementVerification[];
  summary?: unknown;
  error?: string;
}

export function useFinanceReadModel(period: string) {
  const [verifications, setVerifications] = useState<SettlementVerification[]>([]);
  const [summary, setSummary] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (period && period !== 'all') params.set('period', period);

      const data = await apiFetch<SettlementResponse>(`/api/finance/settlement-verification?${params.toString()}`);
      if (data?.error) throw new Error(data.error);

      setVerifications(data.verifications || []);
      setSummary(data.summary || null);
    } catch (err) {
      setVerifications([]);
      setSummary(null);
      setError(err instanceof Error ? err.message : 'Finance read load failed');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const statusCounts = useMemo<StatusCounts>(() => {
    const counts: StatusCounts = { total: verifications.length, verified: 0, pending: 0, missing: 0, variance: 0 };
    verifications.forEach((v) => {
      const s = (v.status || '').toLowerCase();
      if (s === 'verified') counts.verified++;
      else if (s === 'pending') counts.pending++;
      else if (s === 'missing') counts.missing++;
      else if (s === 'variance') counts.variance++;
    });
    return counts;
  }, [verifications]);

  return {
    verifications,
    summary,
    statusCounts,
    loading,
    error,
    refreshFinance: load,
  };
}
